import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export type ModelCallLogSuccessFilter = '' | 'false' | 'true'
export type ModelCallLogFallbackFilter = '' | 'true'

export interface ModelCallLog {
  billing_currency: string
  completion_tokens?: number | null
  distribution_key_id?: string | null
  distribution_key_name: string
  error_category?: string | null
  error_message?: string | null
  failure_stage?: string | null
  fallback_pre_active: boolean
  fallback_reason?: string | null
  fallback_triggered_runtime: boolean
  final_public_model_name?: string | null
  final_registered_model_id?: string | null
  final_registered_model_name?: string | null
  finished_at: string
  id: string
  input_cost_amount?: number | string | null
  latency_ms: number
  output_cost_amount?: number | string | null
  output_protocol: string
  pricing_source: string
  prompt_tokens?: number | null
  quota_rule_action?: string | null
  quota_rule_id?: string | null
  quota_rule_message?: string | null
  quota_rule_metric?: string | null
  quota_rule_triggered: boolean
  request_feature: string
  request_id: string
  request_source: string
  requested_public_model_name: string
  resolved_public_model_name?: string | null
  resolved_registered_model_id?: string | null
  resolved_registered_model_name?: string | null
  route_kind: string
  started_at: string
  status_code: number
  success: boolean
  total_cost_amount?: number | string | null
  total_tokens?: number | null
  upstream_interface_type?: string | null
  upstream_provider?: string | null
}

export interface ModelCallLogsListData {
  items: ModelCallLog[]
  page: number
  page_size: number
  total: number
}

export interface ModelCallLogsListParams {
  distributionKeyId?: string
  errorCategory?: string
  failureStage?: string
  fallbackTriggered?: ModelCallLogFallbackFilter
  page: number
  pageSize: number
  provider?: string
  requestId?: string
  requestedPublicModelName?: string
  startedFrom?: string
  startedTo?: string
  success?: ModelCallLogSuccessFilter
}

export interface ModelGatewayUsageOverview {
  avg_latency_ms: number
  completion_tokens: number
  cost_by_currency: Record<string, number | string>
  failure_count: number
  prompt_tokens: number
  request_count: number
  success_count: number
  success_rate: number
  total_tokens: number
}

export interface ModelGatewayUsageOverviewParams {
  startedFrom?: string
  startedTo?: string
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function appendOptionalQuery(params: URLSearchParams, key: string, value: string | undefined) {
  const normalized = value?.trim()

  if (normalized) {
    params.set(key, normalized)
  }
}

function appendOptionalBooleanQuery(
  params: URLSearchParams,
  key: string,
  value: ModelCallLogFallbackFilter | ModelCallLogSuccessFilter | undefined,
) {
  if (value === 'true' || value === 'false') {
    params.set(key, value)
  }
}

export async function listModelCallLogs({
  distributionKeyId,
  errorCategory,
  failureStage,
  fallbackTriggered,
  page,
  pageSize,
  provider,
  requestId,
  requestedPublicModelName,
  startedFrom,
  startedTo,
  success,
}: ModelCallLogsListParams) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  })

  appendOptionalQuery(params, 'request_id', requestId)
  appendOptionalQuery(params, 'distribution_key_id', distributionKeyId)
  appendOptionalQuery(params, 'requested_public_model_name', requestedPublicModelName)
  appendOptionalQuery(params, 'provider', provider)
  appendOptionalBooleanQuery(params, 'success', success)
  appendOptionalBooleanQuery(params, 'fallback_triggered', fallbackTriggered)
  appendOptionalQuery(params, 'error_category', errorCategory)
  appendOptionalQuery(params, 'failure_stage', failureStage)
  appendOptionalQuery(params, 'started_from', startedFrom)
  appendOptionalQuery(params, 'started_to', startedTo)

  const response = await apiClient.request<SuccessEnvelope<ModelCallLogsListData>>(
    `/admin/model-routing/runtime-logs?${params.toString()}`,
  )

  return response.data
}

export async function getModelGatewayUsageOverview({
  startedFrom,
  startedTo,
}: ModelGatewayUsageOverviewParams) {
  const params = new URLSearchParams()

  appendOptionalQuery(params, 'started_from', startedFrom)
  appendOptionalQuery(params, 'started_to', startedTo)

  const query = params.toString()
  const response = await apiClient.request<SuccessEnvelope<ModelGatewayUsageOverview>>(
    `/admin/model-routing/runtime-usage/overview${query ? `?${query}` : ''}`,
  )

  return response.data
}
